/**
 * Time-signature-aware конвертеры tick ↔ MBT (Measure / Beat / Tick).
 *
 * Отличаются от `player-core/timeline.ts` (`tickToSeconds` /
 * `secondsToTick`) тем, что учитывают смену time-signature:
 * «длина такта в тиках» зависит от numerator/denominator, и
 * фиксированное `ticksPerMeasure` для всего файла работать не будет.
 *
 * Используются:
 * - piano-roll: snap-to-grid (1/1, 1/2, 1/4, 1/8, 1/16, 1/32, 1/64 и
 *   триоли) — нужен beat-уровень, а не только time-domain;
 * - slice: границы среза задаются в тактах;
 * - playhead: (Meas, Beat, Tick) counter рядом с MidiPlayer.
 */

import type { MidiTimeSignature } from "@midi-fantom-utils/midi-core";

export type MBT = {
  measure: number;
  beat: number;
  tick: number;
};

export const ticksPerMeasure = (
  ppq: number,
  signature: readonly [number, number]
): number => {
  const [numerator, denominator] = signature;
  if (numerator <= 0 || denominator <= 0) return ppq * 4;
  return Math.max(1, Math.round((numerator * 4 * ppq) / denominator));
};

export const ticksPerBeat = (
  ppq: number,
  signature: readonly [number, number]
): number => {
  const [, denominator] = signature;
  if (denominator <= 0) return ppq;
  return Math.max(1, Math.round((4 * ppq) / denominator));
};

const safePpq = (ppq: number): number =>
  Number.isFinite(ppq) && ppq > 0 ? Math.round(ppq) : 480;

const normalizeTimeSignatures = (
  timeSigs: ReadonlyArray<MidiTimeSignature>
): { ticks: number; timeSignature: [number, number] }[] => {
  const filtered = timeSigs
    .filter((t) => {
      const [n, d] = t.timeSignature;
      return Number.isFinite(n) && Number.isFinite(d) && n > 0 && d > 0;
    })
    .map((t) => ({
      ticks: Math.max(0, Math.round(t.ticks)),
      timeSignature: t.timeSignature,
    }))
    .sort((a, b) => a.ticks - b.ticks);
  if (filtered.length === 0 || filtered[0]!.ticks > 0) {
    return [
      { ticks: 0, timeSignature: [4, 4] as [number, number] },
      ...filtered,
    ];
  }
  return filtered;
};

export const measureBoundaries = (
  ppq: number,
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  totalTicks: number
): number[] => {
  const sigs = normalizeTimeSignatures(timeSigs);
  const safeTotal = Math.max(0, Math.round(totalTicks));
  if (safeTotal === 0) return [0];
  const result: number[] = [0];
  let measureStart = 0;
  let sigIdx = 0; // sigs[sigIdx] is the active signature
  let safety = 1_000_000;
  while (measureStart < safeTotal && safety-- > 0) {
    // measureStart монотонно растёт, поэтому sigIdx только растёт —
    // достаточно одного `while` без re-scan с нуля. O(N + S) вместо
    // O(N × S), что критично при 30 Hz playback tick.
    while (sigIdx + 1 < sigs.length && sigs[sigIdx + 1]!.ticks <= measureStart) {
      sigIdx += 1;
    }
    const [num, den] = sigs[sigIdx]!.timeSignature;
    const tpm = ticksPerMeasure(safePpq(ppq), [num, den]);
    const next = measureStart + tpm;
    if (next >= safeTotal) {
      result.push(safeTotal);
      break;
    }
    result.push(next);
    measureStart = next;
  }
  while (
    result.length > 1 &&
    result[result.length - 1] === result[result.length - 2]
  ) {
    result.pop();
  }
  return result;
};

const findBoundaryIndex = (boundaries: number[], tick: number): number => {
  if (tick <= boundaries[0]!) return 0;
  if (tick >= boundaries[boundaries.length - 1]!) {
    return boundaries.length - 2;
  }
  let lo = 0;
  let hi = boundaries.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (boundaries[mid]! <= tick) lo = mid;
    else hi = mid - 1;
  }
  return Math.max(0, Math.min(boundaries.length - 2, lo));
};

const findSignatureAt = (
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  tick: number
): [number, number] => {
  let sig: [number, number] = [4, 4];
  for (let k = 0; k < timeSigs.length; k += 1) {
    if (timeSigs[k]!.ticks <= tick) {
      sig = timeSigs[k]!.timeSignature;
    } else {
      break;
    }
  }
  return sig;
};

export const tickToMBT = (
  tick: number,
  ppq: number,
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  totalTicks?: number
): MBT => {
  const safeTick = Number.isFinite(tick) ? Math.max(0, Math.round(tick)) : 0;
  const boundaries = measureBoundaries(
    ppq,
    timeSigs,
    totalTicks ?? Number.POSITIVE_INFINITY
  );
  const idx = findBoundaryIndex(boundaries, safeTick);
  const measureStart = boundaries[idx]!;
  const sig = findSignatureAt(timeSigs, measureStart);
  const tpb = ticksPerBeat(safePpq(ppq), sig);
  const localTick = safeTick - measureStart;
  const beat = Math.min(sig[0], Math.floor(localTick / tpb) + 1);
  const tickInBeat = localTick - (beat - 1) * tpb;
  return {
    measure: idx + 1,
    beat,
    tick: tickInBeat,
  };
};

export const mbtToTick = (
  mbt: MBT,
  ppq: number,
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  totalTicks?: number
): number => {
  const boundaries = measureBoundaries(
    ppq,
    timeSigs,
    totalTicks ?? Number.POSITIVE_INFINITY
  );
  const measureIdx = Math.max(1, Math.min(boundaries.length, Math.round(mbt.measure)));
  const measureStart = boundaries[measureIdx - 1] ?? 0;
  const sig = findSignatureAt(timeSigs, measureStart);
  const tpb = ticksPerBeat(safePpq(ppq), sig);
  const maxBeat = sig[0];
  const beat = Math.max(1, Math.min(maxBeat, Math.round(mbt.beat)));
  const tickInBeat = Math.max(0, Math.round(mbt.tick));
  return measureStart + (beat - 1) * tpb + tickInBeat;
};

export const snapTickToMeasure = (
  tick: number,
  ppq: number,
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  totalTicks?: number
): { tick: number; measure: number } => {
  const boundaries = measureBoundaries(
    ppq,
    timeSigs,
    totalTicks ?? Number.POSITIVE_INFINITY
  );
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < boundaries.length; i += 1) {
    const d = Math.abs(boundaries[i]! - tick);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return { tick: boundaries[bestIdx]!, measure: bestIdx + 1 };
};
